import { useIsAlternativeAuction, useNoSelectorVehicleOfferAuctionById } from 'hooks';
import { useViewedVehicle } from '../../hooks/useViewedVehicle';
import { useCurrentUser } from 'hooks/selectors';
import { AUCTION_STATUSES, BUYER_VEHICLE_STATUS, roles, SELLER_VEHICLE_STATUS } from 'constants';
import { auctionOfferStatuses } from 'utils/statusCalculator';
import { OfferAuctionStatus } from 'types/vehicle';

type RenderOptions = {
  render: boolean;
  collapsed: boolean;
};

type HookResponse = {
  bids: RenderOptions;
  offers: RenderOptions;
  alternativeOffers: RenderOptions;
};

const defaultResponse = {
  bids: { render: false, collapsed: false },
  offers: { render: false, collapsed: false },
  alternativeOffers: { render: false, collapsed: false }
};

export const useShouldHistorySectionRender = (): HookResponse => {
  const vehicle = useViewedVehicle();
  const offerAuction = useNoSelectorVehicleOfferAuctionById(vehicle?.id || 0);
  const currentUser = useCurrentUser();
  const isAlternativeAuction = useIsAlternativeAuction(vehicle);

  const status = auctionOfferStatuses(vehicle, offerAuction, currentUser);

  if (!vehicle) return defaultResponse;

  if (isAlternativeAuction) {
    return { ...defaultResponse, alternativeOffers: { render: true, collapsed: false } };
  }
  const showOfferBidsSectionSellerAwarded = (() => {
    if (status !== SELLER_VEHICLE_STATUS.AWARDED) return false;

    const hasPublicOffer = offerAuction?.privateOffers?.some(offer => offer.type === 'public');
    return hasPublicOffer;
  })();

  const showOfferBidsSection = (() => {
    if (!offerAuction?.offerBids || offerAuction.offerBids.length === 0) return false;
    if (currentUser.role === 'admin') return true;
    if (vehicle.auction?.status === AUCTION_STATUSES.ENDED) return false;
    if (currentUser.role === 'buyer')
      return [
        BUYER_VEHICLE_STATUS.AWARDED,
        BUYER_VEHICLE_STATUS.DECLINED,
        BUYER_VEHICLE_STATUS.AWARDED_NOT_CHARGED,
        BUYER_VEHICLE_STATUS.AWARDED_PENDING_CHARGE,
        BUYER_VEHICLE_STATUS.SELLER_ACCEPTING_OFFERS,
        BUYER_VEHICLE_STATUS.TOP_BUYER_SELLER_ACCEPTING_OFFER
      ].includes(status);
    if (currentUser.role === 'seller') {
      return (
        showOfferBidsSectionSellerAwarded ||
        (offerAuction?.status === OfferAuctionStatus.PUBLIC_OFFERS &&
          [
            SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW,
            SELLER_VEHICLE_STATUS.PUBLIC_COUNTER_SENT_TO_BUYERS,
            SELLER_VEHICLE_STATUS.PENDING_ADMIN_APPROVAL,
            SELLER_VEHICLE_STATUS.ACCEPTED_OFFER_PENDING_ADMIN_APPROVAL
          ].includes(status))
      );
    }

    return false;
  })();

  return {
    ...defaultResponse,
    bids: {
      render: vehicle.auction && vehicle.auction.status !== AUCTION_STATUSES.ENDED || currentUser.role === roles.ADMIN,
      collapsed: offerAuction?.status === OfferAuctionStatus.PUBLIC_OFFERS || showOfferBidsSection
    },
    offers: {
      render: showOfferBidsSection,
      collapsed: false
    }
  };
};
